import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useSiteSettings } from '../context/SiteSettingsContext';
import {
  SRS_WHATSAPP_DISPLAY,
  SRS_WHATSAPP_E164,
  buildWhatsAppHref,
} from '../utils/whatsapp';

const HIDDEN_PREFIXES = ['/checkout', '/confirm-identity', '/tenant', '/landlord', '/agent/', '/agency/'];

/**
 * Site-wide WhatsApp entry point — floating action + small help card.
 * Opens wa.me with a prefilled community intro (click-to-chat, no third-party widget SDK).
 */
export default function WhatsappCommunityChat() {
  const { pathname } = useLocation();
  const { get } = useSiteSettings();
  const [open, setOpen] = useState(false);

  const display = get('contact_whatsapp', SRS_WHATSAPP_DISPLAY);
  const e164 = get('contact_whatsapp_e164', SRS_WHATSAPP_E164);

  const pageLabel = useMemo(() => {
    if (pathname === '/') return 'home';
    if (pathname.startsWith('/property/')) return 'property';
    if (pathname.startsWith('/contact')) return 'contact';
    if (pathname.startsWith('/pricing')) return 'pricing';
    return pathname.replace(/^\//, '').split('/')[0] || 'website';
  }, [pathname]);

  const chatHref = useMemo(
    () => buildWhatsAppHref(e164, `Hi SureRightStay, I would like to join the SRS WhatsApp community and get updates on properties and support. (from ${pageLabel})`),
    [e164, pageLabel],
  );

  const hidden = HIDDEN_PREFIXES.some(p => pathname.startsWith(p));

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  if (hidden) return null;

  return (
    <div className="srs-wa-chat" data-open={open ? 'true' : 'false'}>
      {open && (
        <div
          className="srs-wa-chat__panel"
          role="dialog"
          aria-labelledby="srs-wa-chat-title"
          aria-describedby="srs-wa-chat-desc"
        >
          <button
            type="button"
            className="srs-wa-chat__close"
            aria-label="Close WhatsApp options"
            onClick={() => setOpen(false)}
          >
            <i className="fa-solid fa-xmark" aria-hidden />
          </button>
          <div className="srs-wa-chat__badge">
            <i className="fa-brands fa-whatsapp" aria-hidden />
          </div>
          <h2 id="srs-wa-chat-title" className="srs-wa-chat__title">SRS WhatsApp community</h2>
          <p id="srs-wa-chat-desc" className="srs-wa-chat__desc">
            Chat with our team on WhatsApp for community updates, property tips, and quick support — usually faster than email.
          </p>
          <p className="srs-wa-chat__number">{display}</p>
          <a
            className="srs-wa-chat__cta"
            href={chatHref}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setOpen(false)}
          >
            <i className="fa-brands fa-whatsapp" aria-hidden />
            Open in WhatsApp
          </a>
          <p className="srs-wa-chat__fine">
            Opens WhatsApp with a short intro message you can edit before sending.
          </p>
        </div>
      )}

      <button
        type="button"
        className="srs-wa-chat__fab"
        aria-expanded={open}
        aria-label={open ? 'Close WhatsApp community chat' : 'Chat with SRS on WhatsApp'}
        onClick={() => setOpen(v => !v)}
      >
        <i className={`fa-brands fa-whatsapp ${open ? 'srs-wa-chat__fab-icon--hide' : ''}`} aria-hidden />
        <i className={`fa-solid fa-xmark ${open ? '' : 'srs-wa-chat__fab-icon--hide'}`} aria-hidden />
      </button>
    </div>
  );
}

/** Inline CTA for contact page and footers. */
export function WhatsappCommunityLink({
  className = '',
  children,
  pageLabel = 'contact page',
}: {
  className?: string;
  children?: React.ReactNode;
  pageLabel?: string;
}) {
  const { get } = useSiteSettings();
  const display = get('contact_whatsapp', SRS_WHATSAPP_DISPLAY);
  const e164 = get('contact_whatsapp_e164', SRS_WHATSAPP_E164);
  const href = buildWhatsAppHref(
    e164,
    `Hi SureRightStay, I would like to join the SRS WhatsApp community and get updates on properties and support. (from ${pageLabel})`,
  );

  return (
    <a
      className={className}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
    >
      {children ?? (
        <>
          <i className="fa-brands fa-whatsapp" aria-hidden /> Chat on WhatsApp ({display})
        </>
      )}
    </a>
  );
}
